<template>
  <component
    :is="to ? BaseLink : BaseButton"
    ref="wrapperRef"
    v-style="container"
    :aria-current="active"
    :class="brandClasses.root"
    data-test-id="vf-color-picker"
    :to
  >
    <vf-loader v-if="loading" class="absolute z-below-header h-20" :class="colorPickerClasses.swatchLoading" />
    <vf-color-swatch
      v-bind="{ color, unavailable, lazy }"
      class="full duration"
      :class="[swatchClass, { 'rounded-full group-focus-visible:outline-auto': variant === 'round' }]"
      :class-inner="colorPickerClasses.swatchInner"
      :inset="swatchInset"
      :thumbnail="getImageTransformations(thumbnail, config.thumbnail[variant])"
    >
      <span v-if="sale" :class="brandClasses[variant]?.saleDot" :style="brandStyles[variant]?.saleDot" />
      <span v-if="name" class="sr-only">{{ name }}</span>
      <span
        v-if="unavailable"
        v-style="cross"
        :aria-label="$t.outOfStock"
        class="absolute-0"
        :class="[crossClass, { 'rounded-full': variant === 'round' }]"
      />
    </vf-color-swatch>
  </component>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { ColorPickerStyles, ColorPickerTreatment } from '#types/config/components/vf/colorPicker'
import type { ColorSwatchShape } from '#types/product'
import type { SizesSubUnion } from '#types/sizes'
import { BaseButton, BaseLink } from '#components'

export type Props = {
  /**
   * ColorPicker will be a link if this prop is provided
   */
  to?: string
  /**
   * Color value as an array of color codes or 'multicolor'
   */
  color?: string[]
  /**
   * Source for color thumbnail image
   */
  thumbnail?: string
  /**
   * Defines the size of the color picker
   */
  size?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  /**
   * Defines the shape of the color picker
   */
  variant?: ColorSwatchShape
  /**
   * Defines the visual treatment of the color picker
   */
  treatment?: ColorPickerTreatment
  /**
   * Unavailable means the variant is out of stock, it will appear disabled but remain selectable
   */
  unavailable?: boolean
  /**
   * Whether color is selected
   */
  active?: boolean
  /**
   * Whether to lazy load the thumbnail image
   */
  lazy?: boolean
  /**
   * Whether to lazy load the thumbnail image
   */
  loading?: boolean
  /**
   * Required color name for screen reader
   */
  name?: string
  /**
   * mark this color as Sale variant
   */
  sale?: boolean
}

const { active, lazy = true, size = 'md', treatment, unavailable, variant = 'round' } = defineProps<Props>()

const { brandClasses, brandStyles, config } = useAppConfig().components.vf.colorPicker
const wrapperRef = useTemplateRef('wrapperRef')
const colorPickerClasses = computed(() =>
  treatment ? (brandClasses[variant].treatments?.[treatment] ?? brandClasses[variant]) : brandClasses[variant])

const {
  container,
  cross,
  swatch,
  swatchActive,
  swatchUnavailable
} = Object.entries(brandStyles[variant]).reduce((acc: ColorPickerStyles, [key, value]) => {
  acc[key] = {}

  for (const styleProp in value)
    acc[key][styleProp] = replaceValues(isObject(size) ? size : { sm: size }, value[styleProp])

  return acc
}, {})

const swatchClass = computed(() => {
  const c = colorPickerClasses.value
  if (active && unavailable) return c.swatchUnavailableActive ?? c.swatchActive
  if (unavailable) return c.swatchUnavailable ?? c.swatch
  return active ? c.swatchActive : c.swatch
})

const crossClass = computed(() => {
  const c = colorPickerClasses.value
  return active ? (c.crossActive ?? c.cross) : c.cross
})

const swatchInset = computed(() => {
  if (unavailable) return swatchUnavailable?.inset
  return active ? (swatchActive?.inset ?? swatch?.inset) : swatch?.inset
})

defineExpose({
  wrapperRef
})
</script>
